<script lang="ts">
  import { getCategories, addCategory, updateCategory } from '../../lib/storage.js';
  import Notification from '../common/Notification.svelte';
  import Button from '../common/Button.svelte';

  let categories = $state<string[]>([]);
  let newCategoryName = $state('');
  let editingCategory = $state<string | null>(null);
  let editingNewName = $state('');
  let loading = $state(false);
  let error = $state<string | null>(null);
  let success = $state<string | null>(null);
  let searchTerm = $state('');

  $effect(() => {
    loadCategories();
  });

  async function loadCategories() {
    categories = await getCategories();
  }

  const filteredCategories = $derived(() => {
    // Filter out "Uncategorized" - it's a system category users shouldn't manage
    const visibleCategories = categories.filter(c => c !== 'Uncategorized');
    if (!searchTerm) return visibleCategories;
    return visibleCategories.filter(c => c.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  async function handleAddCategory() {
    if (!newCategoryName.trim()) return;

    loading = true;
    const result = await addCategory(newCategoryName.trim());

    if (result.success) {
      await loadCategories();
      newCategoryName = '';
      success = `Added "${newCategoryName.trim()}" successfully!`;
    } else {
      error = result.error || 'Failed to add category';
    }
    loading = false;
  }

  function startEdit(category: string) {
    editingCategory = category;
    editingNewName = category;
  }

  function cancelEdit() {
    editingCategory = null;
    editingNewName = '';
  }

  async function handleUpdateCategory() {
    if (!editingNewName.trim() || editingNewName === editingCategory) {
      cancelEdit();
      return;
    }

    loading = true;
    const result = await updateCategory(editingCategory!, editingNewName.trim());

    if (result.success) {
      await loadCategories();
      success = `Renamed "${editingCategory}" to "${editingNewName.trim()}"`;
      cancelEdit();
    } else {
      error = result.error || 'Failed to rename category';
    }
    loading = false;
  }
</script>

<div class="category-setup">
  {#if error}
    <Notification type="error" message={error} onDismiss={() => error = null} />
  {/if}

  {#if success}
    <Notification type="success" message={success} autoDismiss={true} onDismiss={() => success = null} />
  {/if}

  <div class="intro">
    <p class="intro-text">
      Review and customize the default categories. You can add new ones or rename existing ones to match your preferences.
    </p>
    <p class="intro-hint">
      <strong>Tip:</strong> You can always come back to Settings → Categories to make changes later.
    </p>
  </div>

  <div class="header">
    <input
      type="text"
      bind:value={newCategoryName}
      placeholder="Add new category..."
      disabled={loading}
      class="input"
      onkeydown={(e) => e.key === 'Enter' && handleAddCategory()}
    />
    <Button variant="primary" size="sm" onclick={handleAddCategory} disabled={loading || !newCategoryName.trim()}>
      Add
    </Button>
    <input
      type="text"
      bind:value={searchTerm}
      placeholder="Search..."
      disabled={loading}
      class="input search"
    />
    <span class="count">{filteredCategories().length}</span>
  </div>

  <div class="grid-container">
    <div class="grid">
      {#each filteredCategories() as category (category)}
        <div class="row">
          {#if editingCategory === category}
            <input
              type="text"
              bind:value={editingNewName}
              class="edit-input"
              disabled={loading}
              onkeydown={(e) => {
                if (e.key === 'Enter') handleUpdateCategory();
                if (e.key === 'Escape') cancelEdit();
              }}
            />
            <div class="actions">
              <Button variant="success" size="sm" onclick={handleUpdateCategory} disabled={loading}>Save</Button>
              <Button variant="secondary" size="sm" onclick={cancelEdit} disabled={loading}>Cancel</Button>
            </div>
          {:else}
            <span class="name">{category}</span>
            <div class="actions">
              <Button variant="primary" size="sm" onclick={() => startEdit(category)} disabled={loading}>
                Rename
              </Button>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  </div>

  {#if filteredCategories().length === 0}
    <div class="empty">
      {searchTerm ? `No match for "${searchTerm}"` : 'No categories'}
    </div>
  {/if}
</div>

<style>
  .category-setup {
    max-height: 500px;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
  }

  .intro {
    text-align: left;
    background-color: var(--color-background-secondary);
    border-radius: var(--radius-md);
    padding: var(--spacing-md);
  }

  .intro-text {
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    margin: 0 0 var(--spacing-sm) 0;
    line-height: var(--line-height-normal);
  }

  .intro-hint {
    font-size: var(--font-size-xs);
    color: var(--color-text-tertiary);
    margin: 0;
    line-height: var(--line-height-normal);
  }

  .header {
    display: flex;
    gap: var(--spacing-sm);
    align-items: center;
  }

  .input {
    padding: var(--spacing-sm) var(--spacing-md);
    border: 1px solid var(--color-border-medium);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
  }

  .input:first-of-type {
    flex: 2;
  }

  .input.search {
    flex: 1;
  }

  .input:focus {
    outline: none;
    border-color: var(--color-primary-bright);
  }

  .input:disabled {
    background: var(--color-background-tertiary);
  }

  .count {
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-muted);
    min-width: 20px;
    text-align: right;
  }

  .grid-container {
    flex: 1;
    overflow-y: auto;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: white;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-xs);
    padding: var(--spacing-xs);
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-sm);
    padding: var(--spacing-sm) var(--spacing-md);
    border-radius: var(--radius-sm);
    transition: background var(--transition-fast);
    min-width: 0;
    background: var(--color-background-secondary);
  }

  .row:hover {
    background: var(--color-background-tertiary);
  }

  .name {
    flex: 1;
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  .edit-input {
    flex: 1;
    padding: var(--spacing-xs) var(--spacing-sm);
    border: 1px solid var(--color-border-medium);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    min-width: 0;
  }

  .edit-input:focus {
    outline: none;
    border-color: var(--color-primary-bright);
  }

  .edit-input:disabled {
    background: var(--color-background-tertiary);
  }

  .actions {
    display: flex;
    gap: var(--spacing-xs);
    opacity: 0.5;
    transition: opacity var(--transition-fast);
    flex-shrink: 0;
  }

  .row:hover .actions {
    opacity: 1;
  }

  .empty {
    text-align: center;
    padding: var(--spacing-xl);
    color: var(--color-text-muted);
    font-size: var(--font-size-sm);
    background: var(--color-background-secondary);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
  }

  @media (max-width: 768px) {
    .grid {
      grid-template-columns: 1fr;
    }

    .header {
      flex-wrap: wrap;
    }

    .input:first-of-type {
      flex: 1 1 100%;
    }
  }
</style>
